import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../app/hooks';
import { OrderingOperations } from 'polygon-ordering';
import type { PAYMENT_SESSION_STATUS } from '../libs/polygon-ordering/src/constants/paymentSessionStatus';

const { fetchPaymentSessionStatus, isPaymentSessionResolved } = OrderingOperations;

const POLLING_INTERVAL = 2000;
const DEFAULT_TIMEOUT_MS = 30000;

type PaymentStatus = PAYMENT_SESSION_STATUS | null;

type PaymentSessionResult = {
  status: PaymentStatus;
  reference: string | null;
} | null;

const usePaymentStatusPoller = (reference: string | null, timeoutMs = DEFAULT_TIMEOUT_MS) => {
  const dispatch = useAppDispatch();
  const [sessionResult, setSessionResult] = useState<PaymentSessionResult>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [resumeCount, setResumeCount] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!reference) {
      setSessionResult(null);
      return;
    }

    setTimedOut(false);

    const poll = async () => {
      try {
        const result = await dispatch(fetchPaymentSessionStatus(reference));
        const nextResult: PaymentSessionResult = fetchPaymentSessionStatus.fulfilled.match(result)
          ? result.payload
          : null;
        setSessionResult(nextResult);
        if (isPaymentSessionResolved(nextResult?.status ?? null)) {
          stop();
        }
      } catch {
        // non-terminal fetch errors don't stop polling
      }
    };

    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) {
        stop();
        setTimedOut(true);
      }
    }, timeoutMs);

    poll();
    intervalRef.current = setInterval(poll, POLLING_INTERVAL);

    return stop;
    // resumeCount is a manual re-run trigger (see resumePolling below) — reference alone
    // doesn't change when the user retries against the same session after a timeout.
  }, [reference, resumeCount]);

  const resumePolling = () => {
    if (!reference) return;
    setResumeCount(count => count + 1);
  };

  return {
    status: sessionResult?.status ?? null,
    reference: sessionResult?.reference ?? null,
    isComplete: isPaymentSessionResolved(sessionResult?.status ?? null),
    timedOut,
    resumePolling,
  };
};

export default usePaymentStatusPoller;
